import WavingHandIcon from "@mui/icons-material/WavingHand";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useRecordContext, useRefresh } from "react-admin";
import { draftDunningNotice, postponeDunningProcess } from "../api/backend.api";
import { LocalizedError } from "../utils/LocalizedError";
import { reportError } from "../backoffice.utils";
import { Student, studentsProvider } from "../providers/studentsProvider";
import { DunningProcess, type EmailDraft } from "../generated/backendClient";
import { useDialog } from "../hooks/useDialog";
import { useState } from "react";
import { useMutation } from "react-query";
import { SendDunningNoticeDialog, SendDunningNoticeFormValues } from "../dialogs/SendDunningNoticeDialog";
import { serverAPI } from "../api/server.api";
import { DateTime } from "luxon";
import { EventPropagationStopper } from "../misc/EventPropagationStopper";

export function HandoverToPairFinanceButton({ variant = "rowButton" }: { variant?: "rowButton" | "dialogButton" }) {
  const record = useRecordContext<Student | DunningProcess>();
  const refresh = useRefresh();
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [dunningProcessId, setDunningProcessId] = useState<string>();
  const [draft, setDraft] = useState<EmailDraft | undefined>();

  const draftEmailAndOpenDialog = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftingEmail(true);
    try {
      // Check if the student has a complete postal address ...
      let student: Student;
      if (isDunningProcess(record)) {
        student = (await studentsProvider.getOne("students", { id: record.student })).data;
      } else {
        student = record;
      }
      const { postalAddress } = student;
      if (!(postalAddress && postalAddress.street && postalAddress.postalCode && postalAddress.city)) {
        if (!postalAddress || !(postalAddress.street || postalAddress.postalCode || postalAddress.city)) {
          throw new LocalizedError(`Die Adress des Wohnorts von ${student.name} ist nicht hinterlegt.`);
        } else {
          throw new LocalizedError(`Die Adresse des Wohnorts von ${student.name} ist unvollständig.`);
        }
      }
      // Draft email ...
      const { dunningProcess, draft } = await draftDunningNotice(record, "inkasso_notice");
      if (!dunningProcess.second_dunning_notice) {
        showErrorToast("Es wurde noch keine 2. Mahnung verschickt.");
        return;
      }
      const secondDunningNoticeSentAt = DateTime.fromISO(dunningProcess.second_dunning_notice.email_sent_at);
      const dateDiff = Math.floor(DateTime.now().diff(secondDunningNoticeSentAt, "days").days);
      if (dateDiff < 14) {
        showErrorToast("Die Übergabe an PAIR Finance kann erst 14 Tage nach der 2. Mahnung erfolgen.");
        return;
      }
      setDunningProcessId(dunningProcess.id);
      setDraft(draft);
      openDialog();
    } catch (error) {
      if (error instanceof LocalizedError) {
        showErrorToast(error.message);
      } else {
        reportError("Failed to draft inkasso notice", error);
        showErrorToast(
          "Oje, es ist ein Fehler beim Vorbereiten der Übergabe an PAIR Finance aufgetreten. Bitte wende Dich an die Technik.",
        );
      }
    } finally {
      setDraftingEmail(false);
    }
  };

  const { mutateAsync: handoverToPairFinance } = useMutation(
    async ({ emailSubject, emailText, postponementDate }: SendDunningNoticeFormValues) => {
      if (!dunningProcessId) {
        throw new Error("dunningProcessId not set");
      }
      let student: Student;
      if (isDunningProcess(record)) {
        student = (await studentsProvider.getOne("students", { id: record.student })).data;
      } else {
        student = record;
      }
      await serverAPI.sendDunningNotice({
        student,
        dunningProcessId,
        noticeType: "inkasso_notice",
        emailSubject,
        emailText,
      });
      await postponeDunningProcess(dunningProcessId, student.id, postponementDate);
    },
    {
      onSuccess: () => {
        refresh();
        showSuccessToast("Inkasso erfolgreich gestartet.");
        closeDialog();
      },
      onError: (error) => {
        reportError("Failed to hand over to PAIR Finance", error);
        showErrorToast("Fehler bei der Übergabe an PAIR Finance");
      },
    },
  );

  return (
    <EventPropagationStopper>
      {variant === "rowButton" ? (
        <LoadingButton
          variant="outlined"
          loading={draftingEmail}
          startIcon={<WavingHandIcon />}
          onClick={draftEmailAndOpenDialog}
        >
          Übergabe an PAIR Finance
        </LoadingButton>
      ) : (
        <Button variant="text" disableRipple sx={{ height: "20px" }} onClick={draftEmailAndOpenDialog}>
          Übergabe an PAIR Finance
        </Button>
      )}
      <SendDunningNoticeDialog
        title="Inkasso"
        {...dialogProps}
        initialFormValues={{
          emailSubject: draft?.subject ?? "",
          emailText: draft?.text ?? "",
          postponementDate: DateTime.now().plus({ days: 30 }),
        }}
        onSubmit={handoverToPairFinance}
      />
    </EventPropagationStopper>
  );
}

function isDunningProcess(x: Student | DunningProcess): x is DunningProcess {
  if (x && typeof x === "object") {
    const { id, student, invoices } = x as any;
    return typeof id === "string" && typeof student === "string" && Array.isArray(invoices);
  }
  return false;
}
